import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { clinicInfo } from '../data/mockData';
import { toast } from 'sonner';

const BookAppointment = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    gender: '',
    appointmentDate: '',
    service: '',
    hearAbout: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
 const handleChange = (e) => {
  const { name, value } = e.target;
         if (!formStarted && typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'appointment_form_start', {
            event_category: 'engagement',
            event_label: 'Appointment Form Started',
            page_path: window.location.pathname,
            page_title: document.title,
            device_type: window.innerWidth < 768 ? 'mobile' : 'desktop'
          });
          setFormStarted(true);
        }
      
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
      if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'appointment_submit_attempt', {
            event_category: 'engagement',
            event_label: 'Submit Button Clicked',
            page_path: window.location.pathname,
            page_title: document.title,
            device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
            form_name: 'appointment_form'
          });
        }
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message || 'Appointment request received! We will contact you shortly.');
                      
          // ✅ ADD THIS TRACKING
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'appointment_submit', {
              event_category: 'conversion',
              event_label: 'Appointment Form Submitted',
              page_path: window.location.pathname,
              page_title: document.title,
              service_selected: formData.service,
              source_selected: formData.hearAbout,
              device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
              form_name: 'appointment_form'            
            });
          }
        // Reset form
        setFormData({
          fullName: '',
          phone: '',
          gender: '',
          appointmentDate: '',
          service: '',
          hearAbout: '',
          message: ''
        });
        setFormStarted(false);
      } else {
            if (typeof window !== 'undefined' && window.gtag) {
              window.gtag('event', 'appointment_submit_failed', {
                event_category: 'error',
                event_label: 'Appointment Form Failed',
                page_path: window.location.pathname,
                page_title: document.title,
                service_selected: formData.service,
                source_selected: formData.hearAbout,
                device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
                form_name: 'appointment_form'
              });
            }
          
            toast.error(data.message || 'Failed to submit appointment. Please try again.');
          }
    } catch (error) {
              console.error('Error submitting appointment:', error);
            
              if (typeof window !== 'undefined' && window.gtag) {
                window.gtag('event', 'appointment_submit_error', {
                  event_category: 'error',
                  event_label: 'Appointment Form Exception',
                  page_path: window.location.pathname,
                  page_title: document.title,
                  device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
                  form_name: 'appointment_form'
                });
              }
            
              toast.error('Failed to submit appointment. Please try again or call us directly.');
            } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Dental Implants',
    'Clear Aligners / Invisible Aligners',
    'Root Canal Treatment',
    'Cosmetic Dentistry',
    'Teeth Whitening',
    'Smile Designing',
    'Pediatric Dentistry',
    'General Dentistry',
    'Emergency Dental Care',
    'Consultation'
  ];

  const hearAboutOptions = [
    'Google',
    'Instagram',
    'Facebook',
    'Friend/Referral',
    'Walk-in',
    'Other'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--bg-secondary)',
        padding: '80px 0',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            Book Your Appointment
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Schedule your visit for painless dental care in Mansarovar, Jaipur
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding">
        <div className="container">
          <div className="grid-two-column">
            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="fullName" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="phone" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="gender" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Gender *
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="appointmentDate" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Preferred Appointment Date *
                  </label>
                  <input
                    type="date"
                    id="appointmentDate"
                    name="appointmentDate"
                    value={formData.appointmentDate}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="service" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Service Required *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <option value="">Select service</option>
                    {services.map(service => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="hearAbout" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    How did you hear about us? *
                  </label>
                  <select
                    id="hearAbout"
                    name="hearAbout"
                    value={formData.hearAbout}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <option value="">Select option</option>
                    {hearAboutOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label htmlFor="message" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Additional Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmitting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  {!isSubmitting && <Send size={16} />}
                </button>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div>
              <div style={{
                position: 'sticky',
                top: '100px'
              }}>
                <div style={{
                  background: 'var(--bg-secondary)',
                  padding: '32px',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)',
                  marginBottom: '24px'
                }}>
                  <h3 className="heading-2" style={{ marginBottom: '24px' }}>
                    Contact Information
                  </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <Phone size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <div className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          Phone & WhatsApp
                        </div>
                         <a 
                        href={`tel:${clinicInfo.phone}`}
                        className="body-regular"
                        style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
                        onClick={() => {
                          if (typeof window !== 'undefined' && window.gtag) {
                            window.gtag('event', 'phone_click', {
                              event_category: 'conversion',
                              event_label: 'Book Appointment Page Phone Click',
                              page_path: window.location.pathname,
                              page_title: document.title,
                              device_type: window.innerWidth < 768 ? 'mobile' : 'desktop'
                            });
                          }
                        }}
                      >
                        {clinicInfo.phone}
                      </a>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                      <Mail size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <div className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          Email
                        </div>
                        <a 
                          href={`mailto:${clinicInfo.email}`}
                          className="body-regular"
                          style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
                          onClick={() => {
                            if (typeof window !== 'undefined' && window.gtag) {
                              window.gtag('event', 'email_click', {
                                event_category: 'conversion',
                                event_label: 'Book Appointment Page Email Click',
                                page_path: window.location.pathname,
                                page_title: document.title,
                                device_type: window.innerWidth < 768 ? 'mobile' : 'desktop'
                              });
                            }
                          }}
                        >
                          {clinicInfo.email}
                        </a>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                      <MapPin size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <div className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          Address
                        </div>
                        <div className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {clinicInfo.address}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                      <Clock size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <div className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          Working Hours
                        </div>
                        <div className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {clinicInfo.workingHours}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{
                  background: 'var(--bg-secondary)',
                  padding: '32px',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)'
                }}>
                  <h3 className="heading-2" style={{ marginBottom: '16px' }}>
                    Quick Booking
                  </h3>
                  <p className="body-regular" style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
                    Prefer instant communication? Book via WhatsApp for quick responses.
                  </p>
                  <a 
                  href={`https://wa.me/${clinicInfo.phone.replace(/[^0-9]/g, '')}?text=Hello! I would like to book an appointment.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ 
                    width: '100%',
                    justifyContent: 'center',
                    background: '#25D366',
                    border: '1px solid #25D366',
                    color: 'white'
                  }}
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'whatsapp_click', {
                        event_category: 'conversion',
                        event_label: 'Book Appointment Page WhatsApp Click',
                        page_path: window.location.pathname,
                        page_title: document.title,
                        device_type: window.innerWidth < 768 ? 'mobile' : 'desktop'
                      });
                    }
                  }}
                >
                  Book via WhatsApp
                </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookAppointment;
